import { z } from 'zod';
import { CursorPageQuery, Email, IsoDate, IsoDateTime, Locale, PhoneE164, Uuid, Version, } from './common.js';
import { ModuleCode, ModuleStatus, PlanCode, SubscriptionStatus } from './modules.js';
import { BillingPeriod, PlanLimits, SubscriptionBillingFields, SubscriptionBillingMode, } from './subscription-billing.js';
import { EstablishmentActivity, TenantStatus } from './tenant.js';
import { DeliveryLink, UserStatus } from './users.js';
/**
 * Console plateforme (ADR-021, phase H6) : administration des tenants, de leurs
 * abonnements, de leurs modules et de leur premier Owner par les opérateurs du
 * tenant technique `platform`. Routes `/platform/*`, permissions `platform.*`.
 *
 * La console ne voit que des **métadonnées** (fiche, abonnement, modules,
 * établissements, comptes) : jamais de réservation, de client, de facture, de
 * commande ni de stock (R-PLT-01).
 */
/** Identifiant public du tenant : minuscules, chiffres et tirets, immuable, jamais `platform` (R-PLT-05). */
export const TenantSlug = z
    .string()
    .regex(/^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])$/, 'Minuscules, chiffres et tirets (3 à 40 caractères)')
    .refine((value) => value !== 'platform', 'Identifiant réservé');
export const PlatformSubscription = z.object({
    id: Uuid,
    planCode: PlanCode,
    planName: z.string(),
    status: SubscriptionStatus,
    startsAt: IsoDateTime,
    endsAt: IsoDateTime.nullable(),
    trialEndsAt: IsoDateTime.nullable(),
    /** `MANUAL` : facturé hors plateforme, jamais coupé ; `INVOICED` : facturé par HOREVIA (ADR-023). */
    billingMode: SubscriptionBillingMode,
    billingPeriod: BillingPeriod.nullable(),
    /** Prix négocié par période, dans `currencyCode` (devise du tenant à la souscription). */
    priceAmount: z.number().nullable(),
    currencyCode: z.string().length(3).nullable(),
    currentPeriodStart: IsoDate.nullable(),
    currentPeriodEnd: IsoDate.nullable(),
    nextInvoiceAt: IsoDate.nullable(),
    autoSuspend: z.boolean(),
    pastDueSince: IsoDate.nullable(),
    suspensionScheduledAt: IsoDate.nullable(),
    /** Surcharge des limites du plan (`null` = limites du plan). */
    limits: PlanLimits.nullable(),
    /** Limites effectives (surcharge sinon plan). */
    effectiveLimits: PlanLimits,
    /** Notes internes de la plateforme (contrat, contact commercial, conditions). */
    notes: z.string().nullable(),
    version: Version,
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
/** Ligne de la liste des tenants. */
export const PlatformTenantSummary = z.object({
    id: Uuid,
    slug: z.string(),
    legalName: z.string(),
    tradeName: z.string().nullable(),
    status: TenantStatus,
    countryCode: z.string().length(2),
    currencyCode: z.string().length(3),
    timezone: z.string(),
    defaultLocale: Locale,
    /** Abonnement vivant (`TRIAL` / `ACTIVE` / `PAST_DUE`), `null` si suspendu ou résilié. */
    subscription: PlatformSubscription.nullable(),
    /** Modules effectivement activés au tenant. */
    modules: z.array(ModuleCode),
    propertiesCount: z.number().int().nonnegative(),
    usersCount: z.number().int().nonnegative(),
    /** Dernière connexion d'un utilisateur du tenant, tous comptes confondus. */
    lastLoginAt: IsoDateTime.nullable(),
    version: Version,
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const PlatformTenantModule = z.object({
    code: ModuleCode,
    name: z.string(),
    status: ModuleStatus,
    dependsOn: z.array(ModuleCode),
    /** Inclus dans le plan de l'abonnement vivant. */
    inPlan: z.boolean(),
    enabled: z.boolean(),
    source: z.enum(['PLAN', 'ADDON']).nullable(),
});
export const PlatformTenantProperty = z.object({
    id: Uuid,
    code: z.string(),
    name: z.string(),
    timezone: z.string(),
    activities: z.array(EstablishmentActivity),
    isActive: z.boolean(),
});
/** Compte porteur du rôle système `owner` dans le tenant. */
export const PlatformTenantOwner = z.object({
    id: Uuid,
    email: Email,
    fullName: z.string(),
    status: UserStatus,
    mfaEnabled: z.boolean(),
    lastLoginAt: IsoDateTime.nullable(),
    /** Une invitation non acceptée et non expirée existe pour ce compte. */
    invitationPending: z.boolean(),
});
/** Fiche complète d'un tenant vue de la console. */
export const PlatformTenant = PlatformTenantSummary.extend({
    moduleCatalog: z.array(PlatformTenantModule),
    properties: z.array(PlatformTenantProperty),
    owners: z.array(PlatformTenantOwner),
    /** Historique des abonnements, du plus récent au plus ancien. */
    subscriptions: z.array(PlatformSubscription),
});
export const ListPlatformTenantsQuery = CursorPageQuery.extend({
    /** Recherche sur l'identifiant, la raison sociale et le nom commercial. */
    q: z.string().max(100).optional(),
    status: TenantStatus.optional(),
    planCode: PlanCode.optional(),
});
/** Provisionnement d'un tenant (D-H37) : fiche, premier établissement, abonnement, Owner invité. */
export const CreatePlatformTenantRequest = z.object({
    slug: TenantSlug,
    legalName: z.string().min(2).max(200),
    tradeName: z.string().max(200).nullable().default(null),
    countryCode: z.string().length(2).toUpperCase(),
    currencyCode: z.string().length(3).toUpperCase(),
    timezone: z.string().min(3).max(60),
    defaultLocale: Locale.default('fr'),
    /** `TRIAL` (essai, `trialEndsAt` conseillé) ou `ACTIVE` (client facturé hors plateforme). */
    status: z.enum(['TRIAL', 'ACTIVE']).default('TRIAL'),
    trialEndsAt: IsoDateTime.nullable().default(null),
    planCode: PlanCode,
    /** Options hors plan (`ADDON`), dépendances comprises dans le plan ou les options. */
    addons: z.array(ModuleCode).default([]),
    notes: z.string().max(2000).nullable().default(null),
    /** Facturation par la plateforme (ADR-023) ; `INVOICED` exige périodicité et prix. */
    billing: SubscriptionBillingFields.default({}),
    property: z.object({
        code: z.string().min(2).max(20),
        name: z.string().min(2).max(120),
        activities: z.array(EstablishmentActivity).min(1),
        /** Défaut : fuseau du tenant. */
        timezone: z.string().min(3).max(60).optional(),
    }),
    owner: z.object({
        email: Email,
        fullName: z.string().min(2).max(120),
        phone: PhoneE164.optional(),
        locale: Locale.default('fr'),
    }),
});
/** Réponse du provisionnement : la fiche et le lien d'activation du Owner (D-055). */
export const CreatePlatformTenantResponse = PlatformTenant.extend({ activation: DeliveryLink });
export const UpdatePlatformTenantRequest = z.object({
    legalName: z.string().min(2).max(200).optional(),
    tradeName: z.string().max(200).nullable().optional(),
    countryCode: z.string().length(2).toUpperCase().optional(),
    currencyCode: z.string().length(3).toUpperCase().optional(),
    timezone: z.string().min(3).max(60).optional(),
    defaultLocale: Locale.optional(),
    version: Version,
});
/** Plan, statut d'essai / actif et dates de l'abonnement vivant (R-PLT-07). */
export const UpdatePlatformSubscriptionRequest = SubscriptionBillingFields.extend({
    planCode: PlanCode,
    status: z.enum(['TRIAL', 'ACTIVE']),
    trialEndsAt: IsoDateTime.nullable().optional(),
    endsAt: IsoDateTime.nullable().optional(),
    notes: z.string().max(2000).nullable().optional(),
    version: Version,
});
export const UpdatePlatformModuleRequest = z.object({ enabled: z.boolean() });
export const SuspendPlatformTenantRequest = z.object({
    reason: z.string().trim().min(3).max(500),
});
/**
 * (Ré)invitation du Owner : nouveau lien pour un compte encore `INVITED`, ou
 * création d'un nouveau compte Owner invité (adresse unique sur la plateforme, R-PLT-04).
 */
export const PlatformOwnerInvitationRequest = z.object({
    email: Email,
    fullName: z.string().min(2).max(120),
    locale: Locale.default('fr'),
});
export const PlatformOwnerInvitationResponse = PlatformTenantOwner.extend({
    activation: DeliveryLink,
});
/** Vue d'ensemble de la plateforme (`GET /platform/overview`). */
export const PlatformOverview = z.object({
    tenants: z.object({
        total: z.number().int().nonnegative(),
        byStatus: z.record(TenantStatus, z.number().int().nonnegative()),
    }),
    subscriptions: z.array(z.object({ planCode: PlanCode, planName: z.string(), count: z.number().int().nonnegative() })),
    modules: z.array(z.object({
        code: ModuleCode,
        name: z.string(),
        status: ModuleStatus,
        tenants: z.number().int().nonnegative(),
    })),
    /** Essais qui expirent sous quatorze jours (ou déjà expirés), du plus proche au plus lointain. */
    trialsEndingSoon: z.array(PlatformTenantSummary),
    recentTenants: z.array(PlatformTenantSummary),
});
//# sourceMappingURL=platform.js.map